// src/components/Workspace/Builder/StepEight.js

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Dumbbell, Loader2 } from 'lucide-react';
import { useWorkout } from '../WorkoutContext';
import { useModal } from '../../../contexts/ModalContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { saveWorkout } from '../../../services/handlers/serviceHandler';

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center h-full min-h-[420px]">
    <Loader2 className="w-16 h-16 text-red-600 animate-spin"
      style={{ filter: 'drop-shadow(0 0 12px rgba(248,7,5,0.6))' }} />
    <p className="mt-6 text-gray-700 font-semibold">Finalizing your workout plan...</p>
    <p className="mt-1 text-sm text-gray-500">Forging every set, rep, and rest.</p>
  </div>
);

const StepEight = () => {
  const navigate = useNavigate();
  const { workoutFinal, workoutStrategy, hasSaved, setHasSaved } = useWorkout();
  const { showModal } = useModal();
  const { userProfile } = useUserProfile();
  const saveAttempted = useRef(false);

  useEffect(() => {
    const handleSaveWorkout = async () => {
      // Prevent multiple save attempts
      if (saveAttempted.current || !workoutFinal || !workoutStrategy || !userProfile?.email || hasSaved) {
        return;
      }

      saveAttempted.current = true;
    
      try {
        const mergedWorkoutData = {
          ...workoutFinal,
          notes: {
            food: workoutStrategy?.notes?.food || '',
            weights: workoutStrategy?.notes?.weights || '',
            repsAndSets: workoutStrategy?.notes?.repsAndSets || ''
          },
          workoutPlan: {
            ...workoutFinal.workoutPlan,
            notes: {
              food: workoutStrategy?.notes?.food || '',
              weights: workoutStrategy?.notes?.weights || '',
              repsAndSets: workoutStrategy?.notes?.repsAndSets || ''
            }
          }
        };
    
    
        await saveWorkout(
          userProfile.email,
          mergedWorkoutData,
          workoutStrategy
        );
    
        setHasSaved(true);
        showModal('Success', 'Your workout plan has been automatically saved!', 'success');
      } catch (error) {
        console.error('Save error:', error);
        saveAttempted.current = false; // Reset on error to allow retry
        showModal('Warning', 'Unable to auto-save your workout plan.', 'warning');
      }
    };

    handleSaveWorkout();
  }, [workoutFinal, workoutStrategy, userProfile?.email, hasSaved, setHasSaved, showModal]);

  // Reset saveAttempted when component unmounts
  useEffect(() => {
    return () => {
      saveAttempted.current = false;
    };
  }, []);

  const handleNavigateToWorkout = () => {
    navigate('/workspace/workout-builder', {
      state: { 
        loadedWorkout: workoutFinal,
        initialTab: 'summary' // Add this to state
      }
    });
  }

  if (!workoutFinal) {
    return <LoadingState />;
  }
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center h-full text-center space-y-8"
    >
      <div className="flex items-center justify-center w-20 h-20 rounded-full"
        style={{
          background: 'linear-gradient(180deg, #ff5a5a 0%, #e30b09 50%, #8a0202 100%)',
          border: '2px solid #4a0000',
          boxShadow:
            'inset 0 2px 0 rgba(255,255,255,0.45), inset 0 -3px 6px rgba(0,0,0,0.3),' +
            '0 6px 14px rgba(200,0,0,0.45), 0 0 30px rgba(248,7,5,0.6)'
        }}
      >
        <CheckCircle className="w-12 h-12 text-white"
          style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.8))' }} />
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-extrabold aw-text-3d-dark">Your Workout Plan is Ready!</h2>
        <p className="text-black/70">
          We've created your personalized workout plan and saved it to your dashboard.
        </p>
      </div>

      <button
        onClick={handleNavigateToWorkout}
        className="aw-btn-3d aw-btn-generate flex items-center justify-center gap-2 px-8 py-4 rounded-2xl
          font-extrabold uppercase text-base"
      >
        <Dumbbell className="w-5 h-5" />
        <span className="aw-text-3d-white">View Your Workout Plan</span>
      </button>

      <div className="text-sm text-black/60">
        <p>You can always access this plan from your dashboard later.</p>
      </div>
    </motion.div>
  );
};

export default StepEight;